import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl, formatNumber } from '../../lib/ep.js';
import { useFilterDataQuery, usePaginator, operators } from '../FilterContent';
import { useOwner } from '../context/context.js';

import ButtonCollection from '../builder/ButtonCollection.js';
import SearchField from '../builder/SearchField.js';
import TabledItemsList, { Column } from '../builder/TabledItemsList.js';
import Tooltip from '../builder/Tooltip.js';

import Module from '../ui_items/Module.js';
import ModuleHeader from '../ui_items/ModuleHeader.js';

import BlockItem from './BlockItem.js';
import IPDetail from './IPDetail.js';

import Paginator from '../utils/Paginator.js';

const ColumnCustomContent = (props) => {
	const display = props?.display ?? true;
	if(display === false) return null;

	return (
		<Column {...props} />
	)
};

const ColumnActions = (props) => {
	return (
		<Column {...props} class="gui_text_align_end" />
	)
};

const TableOfContents = ({ items, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const columns = [
		<ColumnCustomContent
			main={true}
			title={i18n("IP address")}
		>{props.ip_content}</ColumnCustomContent>,
		<ColumnCustomContent
			title={i18n("Users")}
		>{props.ip_register_count}</ColumnCustomContent>,
		<ColumnCustomContent
			title={i18n("Participations")}
		>{props.ip_participate_count}</ColumnCustomContent>,
		<ColumnCustomContent
			title={i18n("Blocked actions")}
		>{props.ip_alerts}</ColumnCustomContent>,
		<ColumnActions
			type='actions'
		>{props.actions}</ColumnActions>,
	];

	return (
		<TabledItemsList
			table_id='ips'
			items={items}
			columns={columns}
			empty_message={props.empty_message}
			max_height='calc(100dvh - 390px - 1em)'
			//sticky_actions={true}
			column_defs={{
				gridTemplateColumns:props?.column_defs ?? '1fr 150px 120px 150px 90px',
			}}
			_class={{ other:'not_in_top' }}
		/>
	)
};

const SecurityCenterIPs = ({ ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const owner = useOwner();
	const promotion = owner?.promotion?.Promotion ?? false;
	const alerts = props?.alerts?.data ?? [];

	const [ search_ip, setSearchIP ] = useState("");
	
	const getIPs = () => {
		return Promise.resolve({ data:props?.ips ?? []});
	};

	const [ state, dispatch ] = useFilterDataQuery(`ips${promotion.id}`, getIPs);
	const [ ips, pagination, goToPage ] = usePaginator(state.filtered, 10);

	if(promotion === false) return null;

	const total_users = props?.total_users ?? 1000;
	const total_participants = props?.total_participants ?? 10000;
	
	const searchIP = (value) => {
		const filter = {
			key:'search_ip',
			name:"IP",
			tier1:['name'],
			tier2:{...operators.textOperators},
			tier3:'input',
			type:'string',
		};

		dispatch({
			type:'SETFILTER',
			filter:{
				key:'search_ip',
				value,
				operator:'like_all',
				filter,
			},
			unique:true,
		});

		setSearchIP(value);
	};

	const all_ips = state?.data?.data ?? (state?.data ?? []);
	if(all_ips.length == 0) {
		return (
			<Module>
				<div class="relative_to_container">{i18n("No IPs detected.")}</div>
			</Module>
		)
	}

	return (
		<Module>
			<ModuleHeader
				subtitle={i18n("List of the top 50 IP addresses with the most registered users. Click on an IP address to view all its recorded activity in a single view.")}
				autotranslate={false}
				actions={<>
					<SearchField
						_field_class="in_top"
						name="search_ip"
						value={search_ip}
						change={(k, v) => searchIP(v)}
						placeholder="Search by IP"
						_class={{ first:true }}
					/>
				</>}
			>{`${i18n("IP Address Analysis")} (${all_ips.length})`}</ModuleHeader>

			{(all_ips.length > 0) && (
				<TableOfContents
					items={ips}
					title={`${i18n("Alerts")}`}
					empty_message={i18n("Nothing to show yet.")}
					ip_content={(item) => {
						const ip_alerts = alerts.filter((al) => al.ip == (item?.name ?? '')).map(({created, type, domain}) => ({ created, type, information:domain }));

						return (
							<div class="link_with_filter">
								<IPDetail
									item={item.name}
									is_blocked={(props?.blacklisted_ips ?? []).includes(item.name) ? true : false}
									alerts={ip_alerts}
									all_alerts={alerts}
									promotion_id={promotion.id}
									onBlock={props.onBlock}
									onUnblock={props.onUnblock}
									basename='ips'
									intl={props.intl}
								/>
							</div>
						)
					}}
					ip_alerts={(item) => {
						const ip_alerts = alerts.filter((al) => al.ip == (item?.name ?? '')).map(({created, type, domain}) => ({created,type,information:domain}));

						return (
							<div>
								{(ip_alerts.length > 0) ? ip_alerts.length : '-'}
							</div>
						)
					}}
					ip_register_count={(item) => {
						const percentage = formatNumber(100 * item.users / total_users);

						return (
							<div>
								{item.users} ({percentage}%)
							</div>
						)
					}}
					ip_participate_count={(item) => {
						const percentage = formatNumber(100 * item.participants / total_participants);

						return (
							<div>
								{item.participants} ({percentage}%)
							</div>
						)
					}}
					actions={(item) => {
						const ip_alerts = alerts.filter((al) => al.ip == (item?.name ?? '')).map(({created, type, domain}) => ({created,type,information:domain}));
						const is_blocked = (props?.blacklisted_ips ?? []).includes(item.name) ? true : false;

						return (
							<ButtonCollection _class={{ other:'gui_flex_end' }}>
								<IPDetail
									item={item.name}
									is_blocked={is_blocked}
									alerts={ip_alerts}
									all_alerts={alerts}
									promotion_id={promotion.id}
									caller_type='button'
									caller_props={{
										children:null,
										icon:'knk-visible',
										squared:true,
									}}
									basename='ips'
									onBlock={props.onBlock}
									onUnblock={props.onUnblock}
									intl={props.intl}
								/>

								<BlockItem
									type='ip'
									is_blocked={is_blocked}
									item={item.name}
									promotion_id={promotion.id}
									onBlock={props.onBlock}
									onUnblock={props.onUnblock}
									button_props={{
										squared:true,
										children:null,
										tooltip:<Tooltip with_icon={false} position='top'>{(!is_blocked) ? "Block IP" : "Unblock IP"}</Tooltip>,
									}}
								/>
							</ButtonCollection>
						)
					}}
					intl={props.intl}
				/>
			)}

			{(pagination.total_pages > 1) && (
				<Paginator
					{...pagination}
					goToPage={goToPage}
				/>
			)}
		</Module>
	);
}

export default injectIntl(SecurityCenterIPs);